import {
  getTabLabel,
  hasLegacySubTabQuery,
  parseTabId,
  resolveTabId,
  type TabItem,
} from "@/utils/tabQuery";

export type PropertyTabItem = TabItem;

export type PrivateAreaTabKey = "section" | "tenant" | "repair" | "contract";

export type PrivateAreaTenantSubTabKey = "contract-info" | "payment";

export const PRIVATE_AREA_TAB_KEYS_FULL: PrivateAreaTabKey[] = [
  "section",
  "tenant",
  "contract",
  "repair",
];

export const PRIVATE_AREA_TAB_KEYS_NON_REVENUE: PrivateAreaTabKey[] = [
  "section",
  "repair",
];

export const PRIVATE_AREA_TAB_INDICES = {
  SECTION: 0,
  TENANT: 1,
  CONTRACT: 2,
  REPAIR: 3,
} as const;

export const PRIVATE_AREA_TABS_FULL = [
  { id: PRIVATE_AREA_TAB_INDICES.SECTION, label: "詳細情報" },
  { id: PRIVATE_AREA_TAB_INDICES.TENANT, label: "テナント管理" },
  { id: PRIVATE_AREA_TAB_INDICES.CONTRACT, label: "契約履歴" },
  { id: PRIVATE_AREA_TAB_INDICES.REPAIR, label: "工事履歴" },
] as const satisfies readonly PropertyTabItem[];

export const PRIVATE_AREA_TABS_NON_REVENUE = [
  { id: 0, label: "詳細情報" },
  { id: 1, label: "工事履歴" },
] as const satisfies readonly PropertyTabItem[];

export const PRIVATE_AREA_TENANT_SUBTABS = [
  { id: 0, label: "テナント情報" },
  { id: 1, label: "入金管理" },
] as const satisfies readonly PropertyTabItem[];

export const PRIVATE_AREA_TENANT_SUBTAB_KEYS: PrivateAreaTenantSubTabKey[] = [
  "contract-info",
  "payment",
];

export const DEFAULT_PRIVATE_AREA_TAB_INDEX = PRIVATE_AREA_TAB_INDICES.SECTION;
export const DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX = 0;

export const buildPrivateAreaTabSearch = ({
  tab = DEFAULT_PRIVATE_AREA_TAB_INDEX,
  subtab,
}: {
  tab?: number;
  subtab?: number;
} = {}) => {
  const params = new URLSearchParams();
  params.set("tab", String(tab));
  if (subtab != null) {
    params.set("subtab", String(subtab));
  }
  return `?${params.toString()}`;
};

export const PRIVATE_AREA_TENANT_CONTRACT_TAB_SEARCH = buildPrivateAreaTabSearch(
  {
    tab: PRIVATE_AREA_TAB_INDICES.TENANT,
    subtab: DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
  },
);

export const PRIVATE_AREA_SECTION_TAB_SEARCH = buildPrivateAreaTabSearch({
  tab: PRIVATE_AREA_TAB_INDICES.SECTION,
});

export const getPrivateAreaTabs = (isRevenueUnit: boolean) =>
  isRevenueUnit ? PRIVATE_AREA_TABS_FULL : PRIVATE_AREA_TABS_NON_REVENUE;

export const getPrivateAreaTabKeys = (
  isRevenueUnit: boolean,
): PrivateAreaTabKey[] =>
  isRevenueUnit
    ? PRIVATE_AREA_TAB_KEYS_FULL
    : PRIVATE_AREA_TAB_KEYS_NON_REVENUE;

export const getPrivateAreaTabConfig = (isRevenueUnit: boolean) =>
  getPrivateAreaTabKeys(isRevenueUnit).map((id, index) => ({
    id,
    label: getPrivateAreaTabs(isRevenueUnit)[index]?.label ?? "",
  }));

export const getPrivateAreaTenantTabIndex = (isRevenueUnit: boolean) =>
  isRevenueUnit ? PRIVATE_AREA_TAB_KEYS_FULL.indexOf("tenant") : -1;

export const isStrictlyInvalidPrivateAreaTabQuery = (
  searchParams: URLSearchParams,
): boolean => {
  if (hasLegacySubTabQuery(searchParams)) return true;

  const tabParam = searchParams.get("tab");
  if (
    tabParam != null &&
    resolveTabId(tabParam, PRIVATE_AREA_TABS_FULL) == null
  ) {
    return true;
  }

  const subtabParam = searchParams.get("subtab");
  if (
    subtabParam != null &&
    resolveTabId(subtabParam, PRIVATE_AREA_TENANT_SUBTABS) == null
  ) {
    return true;
  }

  return false;
};

export const isValidPrivateAreaTabQuery = (
  searchParams: URLSearchParams,
  isRevenueUnit: boolean,
): boolean => {
  if (hasLegacySubTabQuery(searchParams)) return false;

  const tabs = getPrivateAreaTabs(isRevenueUnit);
  const tabParam = searchParams.get("tab");
  const subtabParam = searchParams.get("subtab");

  const tabIndex =
    tabParam == null
      ? DEFAULT_PRIVATE_AREA_TAB_INDEX
      : resolveTabId(tabParam, tabs);
  if (tabIndex == null) return false;

  const tenantTabIndex = getPrivateAreaTenantTabIndex(isRevenueUnit);
  if (tenantTabIndex >= 0 && tabIndex === tenantTabIndex) {
    return (
      subtabParam == null ||
      resolveTabId(subtabParam, PRIVATE_AREA_TENANT_SUBTABS) != null
    );
  }

  return subtabParam == null;
};

export { getTabLabel, parseTabId, resolveTabId };
